/** @format */

import { format, parse } from "date-fns";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaBoxOpen, FaCreditCard, FaEye } from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { get_orders } from "../../store/reducers/orderReducer";
import { devise, ExpirationTimer } from "../../utils/utils";
import Paginations from "../Paginations";

// ─── Constants ───────────────────────────────────────────────────────────────

const STATUS_FILTERS = [
  { value: "all", labelKey: "All" },
  { value: "placed", labelKey: "Placed" },
  { value: "pending", labelKey: "Pending" },
  { value: "paid", labelKey: "Paid" },
  { value: "cancelled", labelKey: "Cancelled" },
  { value: "warehouse", labelKey: "Warehouse" },
];

const PAYMENT_BADGE = {
  paid: {
    bg: "bg-emerald-50",
    text: "text-emerald-700",
    ring: "ring-emerald-200",
    dot: "bg-emerald-500",
  },
  pending: {
    bg: "bg-amber-50",
    text: "text-amber-700",
    ring: "ring-amber-200",
    dot: "bg-amber-500",
  },
  unpaid: {
    bg: "bg-red-50",
    text: "text-red-700",
    ring: "ring-red-200",
    dot: "bg-red-500",
  },
  default: {
    bg: "bg-slate-50",
    text: "text-slate-600",
    ring: "ring-slate-200",
    dot: "bg-slate-400",
  },
};

const DELIVERY_BADGE = {
  delivered: {
    bg: "bg-emerald-50",
    text: "text-emerald-700",
    ring: "ring-emerald-200",
    dot: "bg-emerald-500",
  },
  placed: {
    bg: "bg-blue-50",
    text: "text-blue-700",
    ring: "ring-blue-200",
    dot: "bg-blue-500",
  },
  warehouse: {
    bg: "bg-violet-50",
    text: "text-violet-700",
    ring: "ring-violet-200",
    dot: "bg-violet-500",
  },
  cancelled: {
    bg: "bg-red-50",
    text: "text-red-700",
    ring: "ring-red-200",
    dot: "bg-red-500",
  },
  pending: {
    bg: "bg-amber-50",
    text: "text-amber-700",
    ring: "ring-amber-200",
    dot: "bg-amber-500",
  },
  default: {
    bg: "bg-slate-50",
    text: "text-slate-600",
    ring: "ring-slate-200",
    dot: "bg-slate-400",
  },
};

// ─── Sub-components ──────────────────────────────────────────────────────────

const StatusBadge = ({ value, map }) => {
  const style = map[value?.toLowerCase()] || map.default;
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ring-1 ${style.bg} ${style.text} ${style.ring}`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${style.dot}`} />
      {value ?? "—"}
    </span>
  );
};

const ViewButton = ({ orderId, label }) => (
  <Link
    to={`/dashboard/order/details/${orderId}`}
    className='
      inline-flex items-center gap-1.5 px-3 py-1.5
      bg-white border border-slate-200 text-slate-700
      rounded-lg text-xs font-medium
      hover:border-slate-400 hover:bg-slate-50 hover:shadow-sm
      transition-all duration-150
    '
    title={label}
  >
    <FaEye className='text-slate-500 text-[11px]' />
    {label}
  </Link>
);

const PayButton = ({ onClick, label }) => (
  <button
    type='button'
    onClick={onClick}
    className='
      inline-flex items-center gap-1.5 px-3 py-1.5
      bg-blue-600 text-white
      rounded-lg text-xs font-semibold
      hover:bg-blue-700 hover:shadow-md hover:shadow-blue-200
      active:scale-95
      transition-all duration-150
    '
    title={label}
  >
    <FaCreditCard className='text-[11px]' />
    {label}
  </button>
);

const EmptyState = ({ t }) => (
  <tr>
    <td colSpan={6}>
      <div className='flex flex-col items-center justify-center py-14 text-slate-400 gap-3'>
        <FaBoxOpen className='text-4xl text-slate-300' />
        <p className='text-sm font-medium'>{t("No orders found")}</p>
      </div>
    </td>
  </tr>
);

// ─── Main Component ───────────────────────────────────────────────────────────

const MyOrders = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { customerInfo } = useSelector((state) => state.auth);
  const { myOrders, parPage, totalOrder } = useSelector((state) => state.order);

  const [statusFilter, setStatusFilter] = useState("all");
  const [pageNumber, setPageNumber] = useState(1);

  // Reset page to 1 when filter changes
  const handleFilterChange = useCallback((value) => {
    setStatusFilter(value);
    setPageNumber(1);
  }, []);

  const formatDate = useCallback((dateString) => {
    try {
      const parsed = parse(dateString, "MMMM d, yyyy hh:mm a", new Date());
      return format(parsed, "MMM. d, yyyy");
    } catch {
      return dateString ?? "—";
    }
  }, []);

  const redirect = useCallback(
    (order) => {
      navigate("/payment", {
        state: {
          price: order.price,
          items: order.quantity,
          orderId: order._id,
        },
      });
    },
    [navigate],
  );

  useEffect(() => {
    dispatch(
      get_orders({
        status: statusFilter,
        customerId: customerInfo.id,
        pageNumber,
      }),
    );
  }, [dispatch, statusFilter, customerInfo.id, pageNumber]);

  const showPagination = totalOrder > parPage;

  return (
    <div className='bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden'>
      {/* ── Header ── */}
      <div className='flex flex-col justify-between gap-4 px-6 py-5 border-b border-slate-100'>
        <div className='flex items-center gap-3'>
          <h2 className='text-lg font-bold text-slate-800'>{t("My Orders")}</h2>
          {totalOrder > 0 && (
            <span className='bg-slate-100 text-slate-600 text-xs font-semibold px-2.5 py-0.5 rounded-full'>
              {totalOrder}
            </span>
          )}
        </div>
        <hr />
        {/* ── Filter Pills ── */}
        <div className='flex flex-wrap gap-1.5'>
          {STATUS_FILTERS.map(({ value, labelKey }) => (
            <button
              key={value}
              type='button'
              onClick={() => handleFilterChange(value)}
              className={`
                px-3 py-1 rounded-full text-xs font-medium transition-all duration-150
                ${
                  statusFilter === value
                    ? "bg-blue-600 text-white shadow-sm shadow-blue-200"
                    : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }
              `}
            >
              {t(labelKey)}
            </button>
          ))}
        </div>
      </div>
      <hr />
      {/* ── Table ── */}
      <div className='overflow-x-auto'>
        <table className='w-full text-sm text-left'>
          <thead className='bg-slate-50 border-b border-slate-100'>
            <tr>
              {[
                t("Order ID"),
                t("Price"),
                t("Payment Status"),
                t("Order Status"),
                "Date",
                "Actions",
              ].map((col) => (
                <th
                  key={col}
                  scope='col'
                  className='px-5 py-3.5 text-xs font-semibold text-slate-500 uppercase tracking-wide whitespace-nowrap'
                >
                  {col}
                </th>
              ))}
            </tr>
          </thead>

          <tbody className='divide-y divide-slate-50'>
            {myOrders.length === 0 ? (
              <EmptyState t={t} />
            ) : (
              myOrders.map((item) => (
                <tr
                  key={item._id}
                  className='hover:bg-slate-50/70 transition-colors duration-100'
                >
                  {/* Order ID */}
                  <td className='px-5 py-4 whitespace-nowrap'>
                    <span className='font-mono text-xs font-semibold text-slate-500 bg-slate-100 px-2 py-0.5 rounded'>
                      #{item._id?.slice(-8).toUpperCase()}
                    </span>
                  </td>

                  {/* Price */}
                  <td className='px-5 py-4 whitespace-nowrap'>
                    <span className='font-bold text-slate-800'>
                      {item.price?.toFixed(2)}
                    </span>{" "}
                    <span className='text-xs text-slate-400'>{devise}</span>
                  </td>

                  {/* Payment Status */}
                  <td className='px-5 py-4 whitespace-nowrap'>
                    <StatusBadge
                      value={item.payment_status}
                      map={PAYMENT_BADGE}
                    />
                  </td>

                  {/* Delivery Status */}
                  <td className='px-5 py-4 whitespace-nowrap'>
                    <StatusBadge
                      value={item.delivery_status}
                      map={DELIVERY_BADGE}
                    />
                  </td>

                  {/* Date */}
                  <td className='px-5 py-4 whitespace-nowrap text-slate-500 text-xs'>
                    {formatDate(item.date)}
                  </td>

                  {/* Actions */}
                  <td className='px-5 py-4 whitespace-nowrap'>
                    <div className='flex flex-col gap-2'>
                      {/* Row 1 — primary actions */}
                      <div className='flex items-center gap-2'>
                        <ViewButton orderId={item._id} label={t("View")} />

                        {item.payment_status !== "paid" && (
                          <PayButton
                            onClick={() => redirect(item)}
                            label={t("Pay Now")}
                          />
                        )}

                        {/* Row 3 — expiration timer (only if present) */}
                        {item.expiresAt && (
                          <div className='mt-0.5'>
                            <ExpirationTimer expiresAt={item.expiresAt} />
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* ── Pagination ── */}
      {showPagination && (
        <div className='flex justify-end px-5 py-4 border-t border-slate-100'>
          <Paginations
            pageNumber={pageNumber}
            setPageNumber={setPageNumber}
            totalItem={totalOrder}
            parPage={parPage}
            showItem={Math.floor(totalOrder / parPage) + 2}
          />
        </div>
      )}
    </div>
  );
};

export default MyOrders;
